import { useRouter } from 'next/router';
import React, { useContext } from 'react';
import { FiAlertTriangle, FiCheck, FiX } from 'react-icons/fi';

import useGa from '../../hooks/useGa';
import ROUTES from '../../routes';
import { gaEvent } from '../../utils/ga';
import CTA from '../CTA';
import CustomHead from '../CustomHead';
import Quote from '../features/Quote';
import HeaderSimple from '../HeaderSimple';
import { SectionHeading } from '../landing/sectionComponents';
import FaqsSection from '../pricing/FaqsSection';

import AppGraphic from '@components/AppGraphic';
import Button from '@components/Button';
import CtaForm from '@components/common/CtaForm';
import CustomerLogos from '@components/CustomerLogos';
import FooterNew from '@components/FooterNew';
import Heading from '@components/Heading';
import Section from '@components/Section';
import { Context } from '@contexts/AppContext';

const ComparisonFormat = ({ data }) => {
  const router = useRouter();
  const { isLoggedIn } = useContext(Context);
  const gaClickFn = useGa(`${data.name} comparison`, 'Hero');

  function onDemoClick() {
    gaEvent('Book a demo', 'Comparison landing page');
  }

  function getOrder(obj) {
    if (obj[0] === 'no' || obj[0] === false) {
      return 0;
    }

    if (obj[0] === 'warning') {
      return -1;
    }

    if (obj[0] === 'checked' || obj[0] === true) {
      return -2;
    }

    if (obj[0] === 'skip') {
      return -3;
    }

    return 1;
  }

  const sortedTable = data.table
    .map((obj) => ({
      ...obj,
      order: getOrder(obj.value),
    }))
    .sort(({ order: a }, { order: b }) => b - a);

  return (
    <>
      <CustomHead
        meta={{
          title: `PopSQL vs. ${data.name} | PopSQL`,
          description: `${data.metaDescription}`,
        }}
        openGraph={{
          url: `${process.env.NEXT_PUBLIC_WEB_HOST}${router.asPath}`,
        }}
      />
      <main>
        {!data.lp && (
          <HeaderSimple
            cta={{ text: 'Book a demo', href: ROUTES.demo }}
            secondaryCta={{ text: 'Sign up', href: ROUTES.signUp }}
          />
        )}
        <Section>
          <div className="mx-auto text-center mb-4 gap-4 flex items-center">
            <img
              className="max-h-[100px] mx-auto"
              src={`${process.env.NEXT_PUBLIC_ASSET_HOST}/static/homepage/popsql_app_icon.png`}
              alt="popsql"
            />

            <div className="h-32 w-px mx-8 bg-gray-200 block transform rotate-[24deg]" />

            <img
              className="max-h-[100px] mx-auto"
              src={data.logo}
              alt="popsql"
            />
          </div>

          <SectionHeading
            text={`PopSQL vs. ${data.name}`}
            moreStyles={{ maxWidth: 800 }}
          />
          {data.desc && (
            <div
              style={{ maxWidth: '800px' }}
              className="w-full font-inter font-medium text-lg text-center leading-relaxed text-gray-500 mx-auto mb-4 mt-6"
            >
              {data.desc}
            </div>
          )}

          {isLoggedIn ? (
            <CtaForm />
          ) : (
            <div className="text-center space-y-4 md:space-y-0 md:flex items-center gap-4 mt-6 mb-12">
              <Button href={ROUTES.demo} onClick={onDemoClick}>
                Book a demo
              </Button>

              <Button
                variant="border"
                href={`${process.env.NEXT_PUBLIC_WEB_HOST}/users/sign_up`}
                onClick={gaClickFn}
              >
                Sign up for free
              </Button>
            </div>
          )}

          <AppGraphic />

          <CustomerLogos />
        </Section>

        <Section>
          {data.features.map((i, idx) => {
            return (
              <div
                className={`my-24 lg:flex lg:items-center max-w-5xl ${
                  i.isReverse ? `lg:flex-row-reverse` : ``
                }`}
                key={idx}
              >
                <div
                  className={`lg:w-1/2 lg:flex flex-col justify-center ${
                    i.isReverse ? `lg:ml-16` : `lg:mr-16`
                  }`}
                >
                  <Heading size="sm" className="mb-8">
                    {i.title}
                  </Heading>

                  {i.description && (
                    <div className="font-inter font-medium">
                      {i.description}
                    </div>
                  )}

                  {i.bullets && (
                    <ul className="space-y-8 font-inter font-medium">
                      {i.bullets.map((item, id) => (
                        <li key={id} className="flex gap-6 items-start">
                          <i className="flex w-8 flex-none justify-center py-1">
                            <img
                              className="flex-none"
                              src={item.icon.props.src}
                              alt="icon"
                              width="24"
                              height="24"
                            />
                          </i>
                          <p>{item.text}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="lg:w-1/2 h-full flex items-center justify-center">
                  <img
                    className="w-full block mt-16 lg:mt-0 -mb-16 lg:-mb-32"
                    src={i.image}
                  />
                </div>
              </div>
            );
          })}
        </Section>
        <div>
          {data.quote && (
            <Quote
              title={false}
              quote={data.quote.text}
              quoteSize="text-xl lg:text-2xl"
              author={{
                name: `${data.quote.author}`,
                position: `${data.quote.position}`,
                company: `${data.quote.company}`,
                thumbnail: '',
              }}
              rawLogo
              logo={data.quote.logo}
            />
          )}
        </div>
        <div>
          <div className="px-8 mt-40 mb-16 max-w-3xl mx-auto">
            <Heading size="sm" className="text-center">
              See how PopSQL stacks up against {data.name}
            </Heading>
          </div>

          <div className="px-8 mb-8 max-w-3xl mx-auto text-gray-600 font-medium font-inter">
            {sortedTable.map((i, idx) => {
              return (
                <>
                  {i.value[0] !== 'skip' && (
                    <div
                      className={`${
                        idx === data.table.length - 1 ? `` : `border-b`
                      } grid grid-flow-col grid-cols-3 border-gray-200 py-6`}
                      key={idx}
                    >
                      <span>{i.label}</span>

                      {i.value.map((featureIncluded, x) => {
                        return (
                          <span className="text-center" key={x}>
                            {typeof featureIncluded === 'boolean' ? (
                              featureIncluded ? (
                                <div
                                  className={`text-blue-100 flex justify-center transform scale-150`}
                                >
                                  <FiCheck />
                                </div>
                              ) : (
                                <div className="flex text-pink justify-center transform scale-150">
                                  <FiX />
                                </div>
                              )
                            ) : (
                              <>
                                {featureIncluded === 'checked' && (
                                  <div
                                    className={`text-blue-100 flex justify-center transform scale-150`}
                                  >
                                    <FiCheck />
                                  </div>
                                )}

                                {featureIncluded === 'no' && (
                                  <div className="flex text-pink justify-center transform scale-150">
                                    <FiX />
                                  </div>
                                )}

                                {featureIncluded === 'warning' && (
                                  <div className="flex text-yellow-400 justify-center transform scale-150">
                                    <FiAlertTriangle />
                                  </div>
                                )}

                                {featureIncluded !== 'no' &&
                                  featureIncluded !== 'checked' &&
                                  featureIncluded !== 'warning' &&
                                  featureIncluded}
                              </>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })}
          </div>
        </div>

        <Section>
          {data.faqs && <FaqsSection defaultOpen={false} content={data.faqs} />}

          <CTA
            title={data.cta.title}
            cta={{
              text: 'Book a demo',
              href: ROUTES.demo,
              onClick: onDemoClick,
            }}
            secondaryCta={{
              text: 'Sign up for free',
              href: ROUTES.signUp,
            }}
          />

          {data.disclaimer && (
            <p className="text-gray-400 font-inter font-medium text-xs text-center my-4 z-10">
              {data.disclaimer}
            </p>
          )}
        </Section>
      </main>
      <FooterNew />
    </>
  );
};

export default ComparisonFormat;
